import { z } from 'zod';
import { HotelPensionTypeEnum } from './HotelPensionPricingSchema';

const priceField = z
  .union([z.null(), z.number(), z.string()])
  .optional()
  .transform((val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isNaN(n) ? null : n;
  });

const toDateOrNull = (val: Date | string | null | undefined): Date | null => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const HotelSupplementPricingPeriodSchema = z
  .object({
    id: z.string().optional(),
    startDate: z.union([z.null(), z.string(), z.date()]).optional(),
    endDate: z.union([z.null(), z.string(), z.date()]).optional(),
    applicationDays: z.array(z.string()).optional().default([]),
  })
  .refine(
    (data) => {
      const start = toDateOrNull(data.startDate);
      const end = toDateOrNull(data.endDate);
      if (!start || !end) return true;
      return start <= end;
    },
    { message: 'Les dates de définition de période sont invalides.' },
  );

export const HotelSupplementPriceSchema = z.object({
  id: z.string().optional(),
  adultPrice: priceField,
  childPrice: priceField,
  adolescentPrice: priceField,
});

export const HotelSupplementPricingSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  periods: z.array(HotelSupplementPricingPeriodSchema).optional().default([]),
  pricing: HotelSupplementPriceSchema.optional(),
});

export const HotelSupplementBaseSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  pensionTypes: z.array(HotelPensionTypeEnum).optional().default([]),
  pricingList: z.array(HotelSupplementPricingSchema).optional().default([]),
});

export const HotelSupplementSchema = HotelSupplementBaseSchema.refine(
  (data) => (data.pensionTypes?.length ?? 0) >= 1,
  {
    message:
      'Au moins un type de pension doit être sélectionné pour ce supplément.',
    path: ['pensionTypes'],
  },
);

export type HotelSupplementFormData = z.infer<typeof HotelSupplementSchema>;
export type HotelSupplementPricingFormData = z.infer<
  typeof HotelSupplementPricingSchema
>;
export type HotelSupplementPricingPeriodFormData = z.infer<
  typeof HotelSupplementPricingPeriodSchema
>;
export type HotelSupplementPriceFormData = z.infer<
  typeof HotelSupplementPriceSchema
>;
